<script setup>
import { router } from '@inertiajs/vue3';
import DeleteAction from '@js/components/actions/DeleteAction.vue';
import ActionButton from '@js/components/buttons/ActionButton.vue';
import InputField from '@js/components/fields/InputField.vue';
import SwitchField from '@js/components/fields/SwitchField.vue';
import FormColumn from '@js/components/forms/FormColumn.vue';
import FormContent from '@js/components/forms/FormContent.vue';
import FormFooter from '@js/components/forms/FormFooter.vue';
import FormGrid from '@js/components/forms/FormGrid.vue';
import FormRoot from '@js/components/forms/FormRoot.vue';
import TabLayout from '@js/components/tabs/TabLayout.vue';
import { goBack, useRoute } from '@js/utils/route';
import { toastError, toastSaved } from '@js/utils/toast';
import { reactive, ref, watch } from 'vue';
import { boolean, object, ref as yupRef, string } from 'yup';

const { user, tenants } = defineProps({
    user: {
        required: false,
        type: Object,
    },
    tenants: {
        required: true,
        type: Array,
    },
});

const { route } = useRoute();

const form = reactive({
    lastname: user ? user.lastname : '',
    firstname: user ? user.firstname : '',
    email: user ? user.email : '',
    changePassword: false,
    password: '',
    passwordConfirmation: '',
    superAdmin: user
        ? user.roles.some((role) => role.name === 'super_admin')
        : false,
    admin: user ? user.roles.some((role) => role.name === 'admin') : true,
    multiAccount: user
        ? user.roles.some((role) => role.name === 'multi_account')
        : false,
    tenantIds: user
        ? user.tenants.reduce((acc, tenant) => {
              acc[tenant.id] = true;
              return acc;
          }, {})
        : {},
});

const isSubmitting = ref(false);

const validationSchema = object({
    firstname: string().required('Ce champ est obligatoire.'),
    lastname: string().required('Ce champ est obligatoire.'),
    email: string()
        .required('Ce champ est obligatoire.')
        .email('Veuillez saisir une adresse email valide.'),
    password: string().when('changePassword', {
        is: true,
        then: () => string().required('Ce champ est obligatoire.'),
    }),
    superAdmin: boolean().required('Ce champ est obligatoire.'),
    admin: boolean().required('Ce champ est obligatoire.'),
    multiAccount: boolean().required('Ce champ est obligatoire.'),
    passwordConfirmation: string().when('changePassword', {
        is: true,
        then: () =>
            string()
                .required('Ce champ est obligatoire.')
                .oneOf(
                    [yupRef('password')],
                    'Les mots de passe ne correspondent pas.',
                ),
    }),
});

function submit() {
    isSubmitting.value = true;

    axios
        .post(route('front.postUser', user ? { id: user.id } : {}), {
            email: form.email,
            lastname: form.lastname,
            firstname: form.firstname,
            password:
                form.changePassword || user == null ? form.password : null,
            passwordConfirmation:
                form.changePassword || user == null
                    ? form.passwordConfirmation
                    : null,
            superAdmin: form.superAdmin,
            admin: form.admin,
            multiAccount: form.multiAccount,
            tenantIds: Object.keys(form.tenantIds).filter(
                (key) => form.tenantIds[key],
            ),
        })
        .then(() => {
            toastSaved();

            goBack();
        })
        .finally(() => {
            isSubmitting.value = false;
        })
        .catch((error) => {
            toastError(error);

            router.visit(route('front.users'));
        });
}

watch(
    () => form.superAdmin,
    () => {
        if (form.superAdmin) {
            form.admin = false;
            form.multiAccount = false;
        }
    },
);

watch(
    () => form.admin,
    () => {
        if (form.admin) {
            form.superAdmin = false;
            form.multiAccount = false;
        }
    },
);

watch(
    () => form.multiAccount,
    () => {
        if (form.multiAccount) {
            form.superAdmin = false;
            form.admin = false;
        }
    },
);
</script>

<template>
    <FormRoot
        :validation-schema="validationSchema"
        :submit="submit"
        v-slot="{ errors }"
    >
        <TabLayout
            :tabs="{
                information: {
                    title: 'Informations',
                    contentSlotName: 'information',
                    forceMount: true,
                },
                roles: {
                    title: 'Rôles',
                    contentSlotName: 'roles',
                    forceMount: true,
                },
                ...(form.multiAccount || form.admin
                    ? {
                          customers: {
                              title: 'Clients',
                              contentSlotName: 'customers',
                              forceMount: true,
                          },
                      }
                    : {}),
            }"
        >
            <template #information>
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="2">
                            <InputField
                                v-model="form.firstname"
                                label="Prénom"
                                placeholder="Prénom"
                                field-name="firstname"
                                field-id="firstname"
                                type="text"
                                required
                            />

                            <InputField
                                v-model="form.lastname"
                                label="Nom"
                                placeholder="Nom"
                                field-name="lastname"
                                field-id="lastname"
                                type="text"
                                required
                            />
                        </FormGrid>

                        <FormGrid :columns="1">
                            <InputField
                                v-model="form.email"
                                label="Email"
                                placeholder="Email"
                                field-name="email"
                                field-id="email"
                                type="email"
                                required
                            />
                        </FormGrid>
                    </FormContent>

                    <FormContent>
                        <FormGrid :columns="1">
                            <SwitchField
                                v-if="user"
                                v-model="form.changePassword"
                                label="Changer le mot de passe"
                                field-id="changePassword"
                                field-name="changePassword"
                            />
                        </FormGrid>

                        <FormGrid
                            v-if="form.changePassword || user == null"
                            :columns="2"
                        >
                            <InputField
                                v-model="form.password"
                                label="Mot de passe"
                                placeholder="Mot de passe"
                                field-name="password"
                                field-id="password"
                                type="password"
                                required
                            />

                            <InputField
                                v-model="form.passwordConfirmation"
                                label="Confirmation du mot de passe"
                                placeholder="Confirmation du mot de passe"
                                field-name="passwordConfirmation"
                                field-id="passwordConfirmation"
                                type="password"
                                required
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>

            <template #roles>
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="1">
                            <SwitchField
                                v-model="form.superAdmin"
                                label="Super Admin (Datakepler)"
                                field-id="superAdmin"
                                field-name="superAdmin"
                                inline
                            />

                            <SwitchField
                                v-model="form.admin"
                                label="Admin (Apikom)"
                                field-id="admin"
                                field-name="admin"
                                inline
                            />

                            <SwitchField
                                v-model="form.multiAccount"
                                label="Multi-compte (Client)"
                                field-id="multiAccount"
                                field-name="multiAccount"
                                inline
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>

            <template #customers>
                <FormColumn>
                    <FormContent>
                        <FormGrid :columns="1">
                            <SwitchField
                                v-for="(tenant, index) in tenants"
                                :key="index"
                                v-model="form.tenantIds[tenant.id]"
                                :label="tenant.name"
                                :field-id="'tenantIds[' + tenant.id + ']'"
                                :field-name="'tenantIds[' + tenant.id + ']'"
                                inline
                            />
                        </FormGrid>
                    </FormContent>
                </FormColumn>
            </template>
        </TabLayout>

        <FormFooter :is-submitting="isSubmitting" :errors="errors">
            <template #actions>
                <DeleteAction
                    v-if="user"
                    :url="
                        route('front.deleteUser', {
                            id: user.id,
                        })
                    "
                    :callback="
                        () => {
                            router.visit(route('front.users'));
                        }
                    "
                >
                    <template #trigger>
                        <ActionButton type="secondary">
                            Supprimer
                        </ActionButton>
                    </template>
                </DeleteAction>
            </template>
        </FormFooter>
    </FormRoot>
</template>
